/**
 * Shared custom element registration with property-upgrade support.
 *
 * Custom elements created inside <template> content (e.g. by lit-html) are
 * inert until connected to the DOM.  If a framework sets properties on them
 * before upgrade, the assignment creates instance data-properties that
 * permanently shadow prototype getters/setters.
 *
 * This wrapper detects accessor properties on the class prototype and wraps
 * connectedCallback so that any shadowing instance properties are deleted
 * and re-assigned through the real setter at upgrade time.
 */
export function register(className: string, classType: any) {
    if (customElements.get(className)) return

    const proto = classType.prototype
    const accessorProps: string[] = []
    for (const key of Object.getOwnPropertyNames(proto)) {
        const desc = Object.getOwnPropertyDescriptor(proto, key)
        if (desc && desc.get && desc.set) accessorProps.push(key)
    }

    if (accessorProps.length > 0) {
        const originalConnected = proto.connectedCallback
        proto.connectedCallback = function (this: HTMLElement) {
            for (const prop of accessorProps) {
                if (this.hasOwnProperty(prop)) {
                    const value = (this as any)[prop]
                    delete (this as any)[prop]
                    ;(this as any)[prop] = value
                }
            }
            if (originalConnected) originalConnected.call(this)
        }
    }

    customElements.define(className, classType)
}

// ============================================================================
// Acessor simétrico ao viewModel de componentes
//
// Em KO `@Component`, a instância (vm) fica em property privada do framework
// (Symbol em frame.element). Em Lit `@Component`, fica em `el._instance`.
// Em custom-element puro, a instância É o próprio element. Esses padrões são
// assimétricos e ferramentas externas (tests, devtools, extensões) precisariam
// conhecer o mecanismo interno de cada framework para alcançar o vm.
//
// Centralizamos a chave num Symbol.for global — mesmo entre bundles duplicados,
// `Symbol.for(...)` devolve a mesma instância. Quem cria o componente atribui
// `element[COMPONENT_INSTANCE] = vm`; quem precisa do vm lê o symbol direto ou
// chama `getComponentInstance(element)` (que aplica fallbacks).
// ============================================================================

/** Chave única do vm no host element. Use `Symbol.for` para garantir paridade
 *  entre bundles que carreguem múltiplas cópias deste módulo. */
export const COMPONENT_INSTANCE: unique symbol = Symbol.for('mln.componentInstance') as any

export function setComponentInstance(element: Element | null | undefined, instance: any): void {
    if (!element || instance == null) return
    ;(element as any)[COMPONENT_INSTANCE] = instance
}

export function getComponentInstance(element: Element | null | undefined): any {
    if (!element) return undefined
    return (element as any)[COMPONENT_INSTANCE]
        ?? (element as any)._instance   // Lit @Component fallback caso o registro não tenha ocorrido
        ?? element                       // custom-element puro: a instância é o próprio element
}

// Expor no `window` para ferramentas externas (tests, devtools, extensões).
// Mantém apenas getter/setter — a Symbol é descobrível via `Symbol.for('mln.componentInstance')`.
if (typeof window !== 'undefined') {
    const mln: any = (window as any).millennium ?? ((window as any).millennium = {})
    mln.getComponentInstance ??= getComponentInstance
    mln.setComponentInstance ??= setComponentInstance
}
